import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { backendService } from "../../../services/backend.service";
import type { PbjPrediction, PbjSuggestedPlan, PbjSuggestedPlanRequest } from "../../../utils/models";
import { pbjCachedQueryOptions } from "./pbjQueryOptions";
import { PBJ_MODEL_QUERY_KEY } from "./usePbjModel";

export const usePbjPrediction = (startDate: string, endDate: string) => {
	const { trackError } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	const deptId = chosenDepartment.department_id;
	const query = useQuery<PbjPrediction>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "prediction", deptId, startDate, endDate],
		queryFn: async () => {
			const res = await backendService.getPbjPrediction(deptId, startDate, endDate);
			return res.data;
		},
		enabled: !!deptId && !!startDate && !!endDate && startDate <= endDate,
		// Recalculation is gated by the Calculate button — the mutation invalidates the cache
		// to force a refetch. Otherwise, switching tabs should never refire the request. This is
		// the canonical PBJ cache policy, shared with the Jobs/Breaks/manual-hours caches.
		...pbjCachedQueryOptions,
		// Keep the previous prediction visible while a recalculation is in flight (e.g.
		// after Calculate or a quarter change). Tab switches don't refire the query, so
		// the only time this matters is when the query key changes or it's invalidated.
		placeholderData: keepPreviousData,
	});

	useEffect(() => {
		if (query.error) {
			trackError("pbj_model_calculation_failed", {
				error_message: (query.error as Error)?.message,
			});
		}
	}, [query.error, trackError]);

	return query;
};

// Re-run the "Suggest plan" greedy around manually-added buckets (see
// backendService.postPbjSuggestedPlan). Exposed as a mutation because it's an
// on-demand action, not cached server state — the caller awaits `mutateAsync` when
// the user clicks Suggest (or on first load) and applies the returned steps to the
// recommendation sliders. With an empty `lockedActions` the result equals the
// prediction's pre-computed plan, so callers can skip the round-trip in that case.
export const usePbjSuggestedPlan = () => {
	const { trackError } = useAnalytics();
	return useMutation<PbjSuggestedPlan, Error, PbjSuggestedPlanRequest>({
		mutationFn: async (body) => {
			const res = await backendService.postPbjSuggestedPlan(body);
			return res.data;
		},
		onError: (error) => {
			trackError("pbj_model_suggested_plan_failed", { error_message: error?.message });
		},
	});
};
